import { JobFileCreationDTO } from '@shared/models/job-file-creation.model';
import { DashboardRow } from '../models/dashboard-rows.model';
import { PeriodType } from 'app/modules/dashboard/models/enums/period-type.enum';
import { SupplierTripsFilterInput } from '../models/supplier-trips.model';
import { CustomerTripsFilterInput } from '../models/customer-trips.model';
import { ValuePerKmOrShipmentQueryInput } from '../models/value-per-km-or-shipment.types';
import { CustomerKilometersFilterInput } from '../models/customer-kilometers.model';
import { SupplierListFilterInput, SupplierTimeSeriesFilterInput } from '../models/supplier-list.model';
import { TimeInsightsInput, TimeInsightsOptions } from '../models/time-insights.model';
import {
  ContractedJobItem,
  JobSummaryRequest,
  JobSummaryTimeRange,
  CompletedJobItem,
  DeliveryInProgressJobItem,
  NotifiedJobItem,
  PlannedJobItem
} from '../models/job-summary.model';
import { WorkOrder } from '@control-tower/models/work-order/work-order';
import { VehicleUtilizationMetric } from '../models/vehicle-utilization-metric.model';

export const DASHBOARD_ACTION_PREFIX = '[Dashboard]';

export class FetchOngoingJobs {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Ongoing Jobs`;
  constructor(public pageNumber = 0, public pageSize = 10, public searchText?: string) {}
}

export class FetchSelectedOngoingJob {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Selected Ongoing Job`;
  constructor(public jobRefId: string) {
  }
}

export class FetchTaskFeedSummary {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Task Feed Summary`;
}

export class CreateJobFile {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Creation Job File`;
  constructor(public jobFileCreationModel: JobFileCreationDTO) {}
}

export class CreateHaulagePlan {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Creation Haulage Plan`;
  constructor(
    public haulagePlanCreationModel: JobFileCreationDTO,
    public planningMode: 'optimized' | 'manual',
    /** Orders to attach to the job once it exists. Set when the flow starts from Order Manager. */
    public shipmentOrderSpecIds?: string[]
  ) {}
}
export class FetchInternalCollaborators {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Load Internal Collaborators`;
  constructor(public scopes: string[], public searchText?: string, public pageNum: number = 0) {}
}

export class NavigateToJobFile {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Navigate To Job File`;
  constructor(public jobFileCreationModel: JobFileCreationDTO) {}
}

export class NavigateToHaulagePlanWizard {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Navigate To Haulage Plan Wizard`;
  constructor(public haulagePlanCreationModel: JobFileCreationDTO, public planningMode: 'optimized' | 'manual') {}
}

export class ProcessOrgLogoUrl {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Process Org Logo Url`;
}

export class SearchJobGroupsForJobFileCreation {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Job Groups For Job File Creation`;
  constructor(public searchText?: string) {}
}

export class CreateJobGroup {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Create Job Group`;
  constructor(public name: string) {}
}

export class UpdateDashboardRows {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Update Dashboard Rows`;
  constructor(public dashboardRows: DashboardRow[]) {}
}

export class FetchCustomers {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Customers`;
  constructor(
    public fromDate: Date,
    public toDate: Date
  ) {}
}

export class JobSummaryAction {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Job Summary Action`;
  constructor(
    public readonly status: 'loading' | 'success' | 'error',
    public readonly payload?: {
      request?: JobSummaryRequest;
      /**
       * Ask the server even when the page is cached (the widget's periodic refresh). The rows on
       * screen stay while it loads (no skeleton).
       */
      forceRefresh?: boolean;
      timeRange?: JobSummaryTimeRange;
      data?: {
        completedJobs?: CompletedJobItem[];
        deliveryInProgressJobs?: DeliveryInProgressJobItem[];
        notifiedJobs?: NotifiedJobItem[];
        contractedJobs?: ContractedJobItem[];
        plannedJobs?: PlannedJobItem[];
      };
      error?: string;
    }
  ) {}
}

export class WorkOrderNotificationsAction {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} WorkOrder Notifications Action`;
  constructor(public loadIds: string[]) {}
}

export class CleanupWorkOrderNotificationsAction {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Cleanup WorkOrder Notifications`;
}

/** Clears heavy dashboard state on route leave to reduce memory usage */
export class ClearDashboardStateAction {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Clear Dashboard State`;
}

export class FetchRevenueFinancialData {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Revenue Financial Data`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchCostFinancialData {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Cost Financial Data`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchProfitFinancialData {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Profit Financial Data`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchSupplierTrips {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Supplier Trips`;
  constructor(public filter: SupplierTripsFilterInput) {}
}

export class UpdateJobWorkOrders {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Update Job Work Orders`;
  constructor(public jobRefId: string, public workOrders: WorkOrder[]) {}
}

export class FetchCustomerTrips {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Customer Trips`;
  constructor(public filter: CustomerTripsFilterInput) {}
}

export class FetchCostPerKm {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Cost Per KM`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchCostPerShipment {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Cost Per Shipment`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchRevenuePerKm {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Revenue Per KM`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchRevenuePerShipment {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Revenue Per Shipment`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchProfitPerKm {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Profit Per KM`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class FetchProfitPerShipment {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Profit Per Shipment`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType?: PeriodType
  ) {}
}


export class FetchMostProfitableCustomers {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Most Profitable Customers`;
  constructor(
    public operationId: string,
    public fromDate: Date,
    public toDate: Date,
    public pageSize: number
  ) {}
}

export class FetchCustomerKilometers {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Customer Kilometers`;
  constructor(public filter: CustomerKilometersFilterInput) {}
}

export class FetchSupplierCostAnalysis {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Supplier Cost Analysis`;
  constructor(public filter: import('../models/supplier-costs.model').SupplierCostAnalysisFilterInput) {}
}

export class FetchVehicleUtilizationMetrics {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Vehicle Utilization Metrics`;
  constructor(
    public operationId: string,
    public fromDate: Date,
    public toDate: Date,
    public periodType?: PeriodType
  ) {}
}

export class SetVehicleUtilizationMetrics {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Set Vehicle Utilization Metrics`;
  constructor(public metrics: VehicleUtilizationMetric[]) {}
}

export class UpdateJobSummaryUIState {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Update Job Summary UI State`;
  constructor(public payload: {
    activeTab?: import('../models/job-summary.model').JobStatusEnum;
    timeRange?: import('../models/job-summary.model').TimeRangeEnum;
    dateRange?: {
      startDate: Date;
      endDate: Date;
    };
    pageIndex?: number;
    pageSize?: number;
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc' | '';
  }) {}
}

export class RestoreJobSummaryUIState {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Restore Job Summary UI State`;
}

export class FetchSupplierList {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Supplier List`;
  constructor(public filter: SupplierListFilterInput) {}
}

export class FetchSupplierTimeSeries {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Supplier Time Series`;
  constructor(public filter: SupplierTimeSeriesFilterInput) {}
}

export class FetchOnlineOfflineDeviceCount {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Online Offline Device Count`;
  constructor(public orgId: string) {}
}

export class FetchTimeInsightsData {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Time Insights Data`;
  constructor(
    public orgId: string,
    public operationId: string,
    public startDate: Date,
    public endDate: Date,
    public periodType: PeriodType,
    public options?: TimeInsightsOptions
  ) {}
}

export class FetchBehalfPartnerOrganizations {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Behalf Partner Organizations`;
  constructor(
    public pageNum: number = 0,
    public pageSize: number = 100,
    public searchKey?: string
  ) {}
}

export class FetchBehalfOnboardedOrganizations {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Behalf Onboarded Organizations`;
  constructor(
    public pageNum: number = 0,
    public pageSize: number = 100,
    public searchKey?: string
  ) {}
}

export class FetchBehalfOrgDetails {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Fetch Behalf Organization Details`;
  constructor(public orgId: string) {}
}

export class CreateOperation {
  static readonly type = `${DASHBOARD_ACTION_PREFIX} Create Operation`;
  constructor(public operation: import('app/modules/organization/models/operation').Operation) {}
}

